import React, { useState } from 'react'
import { Modal } from '../../../../common/components/modal/Modal'
import { CaseItem } from './CaseItem'
import { CaseItemModalContent } from './CaseItemModalContent'
import { COUNTRY, currencyConfig } from '../../../../common/utils/constants'
import styles from './Cases.module.scss'

export interface CaseData {
  id: string
  icon: string
  title: string
  industry: string
  profit: {
    before: number
    after: number
    growth: number
    growthPercent: number
  }
  fullData: {
    metrics: Array<{ name: string; before: string | number; after: string | number }>
    drivers: string[]
  }
}



const { symbol, rate } = currencyConfig[COUNTRY] || currencyConfig.ru

const convert = (value: number) => Math.round(value / rate)
const format = (value: number) => `${convert(value).toLocaleString()} ${symbol}`

const casesRu: CaseData[] = [
  {
    id: 'restaurant',
    icon: '',
    title: 'Ресторан',
    industry: 'Общепит',
    profit: { before: 454000, after: 1236000, growth: 782000, growthPercent: 172 },
    fullData: {
      metrics: [
        { name: 'Заказов в месяц', before: '2 000', after: '2 000' },
        { name: 'Доля агрегатора', before: '80%', after: '40%' },
        { name: 'Средний чек (свой канал)', before: 700, after: 1500 },
        { name: 'Выручка /мес', before: 1400000, after: 2360000 },
        { name: 'Комиссия агрегатора /мес', before: 336000, after: 168000 },
        { name: 'Софт (сайт+CRM) /мес', before: 50000, after: 0 },
        { name: 'Валовая прибыль /мес', before: 840000, after: 1416000 },
        { name: 'Операционная прибыль /мес', before: 454000, after: 1236000 },
      ],
      drivers: [
        'Уход от комиссии агрегатора 30% → 0% на своих клиентов',
        `Рост среднего чека с ${format(700)} до ${format(1500)} (за счёт лояльности)`,
        'Своя доставка вместо курьеров агрегатора',
        'Бесплатная CRM и программа лояльности (входят в Logid)',
      ],
    },
  },
  {
    id: 'dealer',
    icon: '',
    title: 'Дилерский центр',
    industry: 'Автобизнес',
    profit: { before: 14725000, after: 17020000, growth: 2295000, growthPercent: 16 },
    fullData: {
      metrics: [
        { name: 'Автомобилей в месяц', before: '30', after: '34' },
        { name: 'Средний чек', before: 2600000, after: 2600000 },
        { name: 'Выручка /мес', before: 78000000, after: 88400000 },
        { name: 'Потери на рекламе /мес', before: 600000, after: 60000 },
        { name: 'CRM (Битрикс24) /мес', before: 50000, after: 0 },
        { name: 'Валовая прибыль /мес', before: 15600000, after: 17680000 },
        { name: 'Операционная прибыль /мес', before: 14725000, after: 17020000 },
      ],
      drivers: [
        'Сокращение потерь на рекламе за счёт атрибуции',
        'Отказ от платной CRM и рассылок (входят в Logid)',
        'Рост конверсии за счёт живых консультаций в чате',
      ],
    },
  },
  {
    id: 'furniture',
    icon: '',
    title: 'Мебельный магазин',
    industry: 'Ритейл',
    profit: { before: 470000, after: 828000, growth: 358000, growthPercent: 76 },
    fullData: {
      metrics: [
        { name: 'Заказов в месяц', before: '1 000', after: '1 200' },
        { name: 'Средний чек', before: 2000, after: 2300 },
        { name: 'Выручка /мес', before: 2000000, after: 2760000 },
        { name: 'Комиссия маркетплейса /мес', before: 200000, after: 0 },
        { name: 'Возвраты /мес', before: 100000, after: 36000 },
        { name: 'Софт (сайт+CRM) /мес', before: 30000, after: 0 },
        { name: 'Валовая прибыль /мес', before: 800000, after: 1104000 },
        { name: 'Операционная прибыль /мес', before: 470000, after: 828000 },
      ],
      drivers: [
        'Уход от комиссии маркетплейса (20%)',
        'Снижение возвратов за счёт своих правил',
        'Рост лояльности и повторных продаж через CRM',
        'Бесплатный сайт и приложение (входят в Logid)',
      ],
    },
  },
]

const applyCurrency = (cases: CaseData[]): CaseData[] =>
  cases.map((c) => ({
    ...c,
    profit: {
      ...c.profit,
      before: convert(c.profit.before),
      after: convert(c.profit.after),
      growth: convert(c.profit.growth),
    },
    fullData: {
      ...c.fullData,
      metrics: c.fullData.metrics.map((m) => ({
        ...m,
        before: typeof m.before === 'number' ? format(m.before) : m.before,
        after: typeof m.after === 'number' ? format(m.after) : m.after,
      })),
      drivers: c.fullData.drivers.map((d) => d.replace(/₽/g, symbol)),
    },
  }))

const casesData = applyCurrency(casesRu)

const isPrerender =
  typeof window !== 'undefined' && window.location.search.includes('__prerender=1')

export const Cases: React.FC = () => {
  const [activeCase, setActiveCase] = useState<CaseData | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleExpand = (caseData: CaseData) => {
    setActiveCase(caseData)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setActiveCase(null)
  }

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <h2 className={styles.title}>Кейсы</h2>

        {isPrerender ? (
          <div className={styles.prerenderGrid}>
            {casesData.map((caseItem) => (
              <CaseItemModalContent key={caseItem.id} data={caseItem} />
            ))}
          </div>
        ) : (
          <>
            <div className={styles.grid}>
              {casesData.map((caseItem) => (
                <CaseItem
                  key={caseItem.id}
                  data={caseItem}
                  onExpand={() => handleExpand(caseItem)}
                />
              ))}
            </div>

            <Modal modalActive={isModalOpen} setModalActive={handleCloseModal}>
              {activeCase ? <CaseItemModalContent data={activeCase} /> : <></>}
            </Modal>
          </>
        )}
      </div>
    </section>
  )
}
